import { useEffect, useCallback, useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { CustomerLoader, Input, PhoneInput, Select, ToggleButtonGroup } from "../../../../../components"
import FileUploadInput from "../../../../../../../common/components/new/file-upload/FileUploadInput"
import styles from "./employment-information.module.scss"
import { validatePhoneNumber } from "../../../../../validators"
import { useLazyGetZambiaCustomerCreditInfoQuery, useSaveZambiaBusinessCustomerCreditInfoMutation, useSaveZambiaEmployeeCustomerCreditInfoMutation } from "../../../../../../../service/approval-service"
import { toast } from "react-toastify"
import { EmploymentCategory, NextStepData } from "../../../../get-solar.types"
import { CreditInformationResponse } from "../../../../../../../service/contract/approval"
import { useDispatch } from "react-redux"
import { setCreditInfo } from "../../../../../../../stores/slices/get-solar-slice"

interface BusinessOwnerFormValues {
  businessName: string
  businessAddress: string
  businessRegistrationNumber: string
  cacCertification: File[]
}

interface EmployeeFormValues {
  fullName: string
  address: string
  phoneNumber: string
  employmentType: string
}

const employmentTypeOptions = [
  { label: 'Full Time', value: 'Full Time' },
  { label: 'Part Time', value: 'Part Time' },
  { label: 'Contract', value: 'Contract' },
]

const toggleOptions: { label: string; value: EmploymentCategory }[] = [
  { label: "Business Owner", value: "businessOwner" },
  { label: "Employee", value: "employee" },
]

interface EmploymentInformationProps {
  onCancel: () => void;
  onContinue: () => void;
  setNextStepData: (data: Partial<NextStepData>) => void;
}

export const EmploymentInformation = ({ onCancel, onContinue, setNextStepData }: EmploymentInformationProps) => {
  const dispatch = useDispatch();

  const [getZambiaCustomerCreditInfoQuery, { isLoading, isFetching }] = useLazyGetZambiaCustomerCreditInfoQuery();
  const [saveBusinessCustomerCreditInfo] = useSaveZambiaBusinessCustomerCreditInfoMutation();
  const [saveEmployeeCustomerCreditInfo] = useSaveZambiaEmployeeCustomerCreditInfoMutation();

  const [employmentCategory, setEmploymentCategory] = useState<EmploymentCategory | undefined>();
  const {
    handleSubmit: handleBusinessOwnerSubmit,
    control: businessOwnerControl,
    reset: resetBusinessOwner,
    formState: { errors: businessOwnerErrors },
  } = useForm<BusinessOwnerFormValues>({
    defaultValues: {
      businessName: "",
      businessAddress: "",
      cacCertification: [],
      businessRegistrationNumber: "",
    },
  })

  const {
    handleSubmit: handleEmployeeSubmit,
    control: employeeControl,
    reset: resetEmployee,
    formState: { errors: employeeErrors },
  } = useForm<EmployeeFormValues>({
    defaultValues: {
      fullName: "",
      address: "",
      employmentType: "",
    },
  })

  const prepopulateData = useCallback((data: CreditInformationResponse) => {
    // 1. Preselect business owner data
    if (data.businessOwnerData != null) {
      setEmploymentCategory('businessOwner');
      setNextStepData({ employmentCategory: 'businessOwner' });

      resetBusinessOwner({
        businessName: data.businessOwnerData.companyName ?? '',
        businessAddress: data.businessOwnerData.companyAddress ?? '',
        businessRegistrationNumber: data.businessOwnerData.pacraCertificationRegNumber ?? '',
        cacCertification: [],
      });
    }

    // 2. Preselect employee data
    if (data.employeeData != null) {
      setEmploymentCategory('employee');
      setNextStepData({ employmentCategory: 'employee' });

      resetEmployee({
        fullName: data.employeeData.fullName ?? '',
        address: data.employeeData.address ?? '',
        phoneNumber: data.employeeData.mobileNumber ?? '',
        employmentType: data.employeeData.employmentType ?? '',
      });
    }
  }, [resetBusinessOwner, resetEmployee, setNextStepData]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await getZambiaCustomerCreditInfoQuery().unwrap();
        dispatch(setCreditInfo(response));
        prepopulateData(response);
      } catch {
        // Pre-population is optional; form remains empty on fetch failure
      }
    };
    fetchTeamMembers();
  }, [getZambiaCustomerCreditInfoQuery, dispatch, prepopulateData]);

  const handleCategoryChange = (value: string) => {
    const category = value as EmploymentCategory;
    setEmploymentCategory(category)
    setNextStepData({ employmentCategory: category })
    resetBusinessOwner()
    resetEmployee()
  }

  const onBusinessOwnerSubmit = async (data: BusinessOwnerFormValues) => {
    const formData = new FormData();
    formData.append('companyName', data.businessName);
    formData.append('companyAddress', data.businessAddress);
    formData.append('pacraCertificationRegNumber', data.businessRegistrationNumber);
    if (data.cacCertification[0]) {
      formData.append('document', data.cacCertification[0]);
    }

    try {
      await saveBusinessCustomerCreditInfo(formData).unwrap();
      onContinue();
    } catch (error) {
      toast.error("Failed to submit business owner information");
    }
  }

  const onEmployeeSubmit = async (data: EmployeeFormValues) => {
    const formData = new FormData();
    formData.append('fullName', data.fullName);
    formData.append('address', data.address);
    formData.append('mobileNumber', data.phoneNumber);
    formData.append('employmentType', data.employmentType);

    try {
      await saveEmployeeCustomerCreditInfo(formData).unwrap();
      onContinue();
    } catch (error) {
      toast.error("Failed to submit employee information");
    }
  }

  const getPageTitle = (category: EmploymentCategory | undefined) => {
    if (!category) return "Select Employment Type";
    return category === "employee" ? "Employment Information" : "Business Information";
  };

  if (isLoading || isFetching) {
    return (
      <CustomerLoader
        title={"Loading Employment Information"}
        subtitle="Please wait while we fetch your information..."
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{getPageTitle(employmentCategory)}</h2>
          {employmentCategory !== undefined && (
            <p className={styles.subtitle}>Verify Employment Information</p>
          )}
        </div>

        <div className={styles.toggleGroupWrapper}>
          <ToggleButtonGroup
            options={toggleOptions}
            value={employmentCategory}
            onChange={handleCategoryChange}
            name="employmentCategory"
            direction="horizontal"
          />
        </div>

        {/*
        {employmentCategory === "businessOwner" && (
          <form className={styles.formContainer} onSubmit={handleBusinessOwnerSubmit(onBusinessOwnerSubmit)}>
            <Controller
              name="businessName"
              control={businessOwnerControl}
              rules={{ required: "Business name is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Business Name*"
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={businessOwnerErrors.businessName?.message}
                />
              )}
            />

            <Controller
              name="businessAddress"
              control={businessOwnerControl}
              rules={{ required: "Business address is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Business Address*"
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={businessOwnerErrors.businessAddress?.message}
                />
              )}
            />

            <Controller
              name="businessRegistrationNumber"
              control={businessOwnerControl}
              rules={{ required: "Business Registration Number is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Business Registration Number*"
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={businessOwnerErrors.businessRegistrationNumber?.message}
                />
              )}
            />

            <Controller
              name="cacCertification"
              control={businessOwnerControl}
              rules={{
                validate: (value) => (value && value.length > 0) || "CAC Certification is required",
              }}
              render={({ field }) => (
                <div className={styles.fileUploadWrapper}>
                  <FileUploadInput
                    label="CAC Certification"
                    required
                    maxFileSize={24}
                    accept=".jpeg,.jpg,.png,.pdf"
                    onFilesSelect={(files) => field.onChange(files)}
                    error={businessOwnerErrors.cacCertification?.message}
                  />
                </div>
              )}
            />

            <div className={styles.footer}>
              <div className={styles.footerInner}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={onCancel}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.continueBtn}
                >
                  Continue
                </button>
              </div>
            </div>
          </form>
        )}
        */}

        {employmentCategory === "employee" && (
          <form className={styles.formContainer} onSubmit={handleEmployeeSubmit(onEmployeeSubmit)}>
            <Controller
              name="fullName"
              control={employeeControl}
              rules={{ required: "Full name is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Full Name*"
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={employeeErrors.fullName?.message}
                />
              )}
            />

            <Controller
              name="address"
              control={employeeControl}
              rules={{ required: "Address is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Address*"
                  value={field.value}
                  onChange={field.onChange}
                  variant="solid"
                  error={employeeErrors.address?.message}
                />
              )}
            />

            <Controller
              name="phoneNumber"
              control={employeeControl}
              rules={{
                required: "Phone number is required",
                validate: (value) => {
                  return validatePhoneNumber(value);
                }
              }}
              render={({ field, fieldState }) => (
                <PhoneInput
                  value={field.value}
                  defaultCountry="zm"
                  onChange={(phone) => {
                    field.onChange(phone)
                  }}
                  placeholder="Enter phone number"
                  error={fieldState.error?.message}
                  className={styles.phoneInput}
                />
              )}
            />

            <Controller
              name="employmentType"
              control={employeeControl}
              rules={{ required: "Please select an employment type" }}
              render={({ field }) => (
                <Select
                  options={employmentTypeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Employment Type*"
                  emptyMessage="No options available"
                  error={employeeErrors.employmentType?.message}
                />
              )}
            />

            <div className={styles.footer}>
              <div className={styles.footerInner}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={onCancel}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.continueBtn}
                >
                  Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
